import * as hashes from "common/hashes";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as analyticsService from "services/analyticsService";
import * as staticDataService from "services/staticDataService";
import * as analyticsStore from "store/analytics";
import useDebouncedValue from "store/hooks/useDebouncedValue";
import * as submissionSelector from "store/selectors/submission/submissionSelector";
import * as userSelectors from "store/selectors/user/userSelectors";

const useSubmissionHash = (submission) => {
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    (async () => {
      const hash = await hashes.hashDict(submission);
      setCurrent(hash);
    })();
  }, [submission, setCurrent]);

  return current;
};

const fetchPricing = (
  triggerAnalytics,
  isUserReadOnly,
  engine,
  metric,
  submission
) => {
  if (isUserReadOnly || !engine) {
    return;
  }

  triggerAnalytics({
    engine,
    metric,
    submission,
  });
};

export const usePricing = ({ pricingMethod } = {}) => {
  pricingMethod = pricingMethod ?? "programPricing";
  const dispatch = useDispatch();

  const submission = useDebouncedValue(
    useSelector(submissionSelector.selectResolvedSubmissionWithoutInput)
  );

  const isUserReadOnly = useSelector(userSelectors.isUserReadOnly);

  const latestSubmissionHash = useSelector(analyticsStore.select)?.[
    pricingMethod
  ]?.submissionHash;

  const submissionHash = useSubmissionHash(submission);

  const { data: analyticsConfig } = staticDataService.useConfigQuery(
    "analytics"
  );
  const pricingConfig = analyticsConfig?.[pricingMethod];

  const [
    triggerAnalytics,
    result,
  ] = analyticsService.useLazySubmissionAnalyticsQuery();

  useEffect(() => {
    if (!latestSubmissionHash || latestSubmissionHash !== submissionHash) {
      fetchPricing(
        triggerAnalytics,
        isUserReadOnly,
        pricingConfig?.engine,
        pricingConfig?.metric,
        submission
      );
    }
  }, [
    triggerAnalytics,
    isUserReadOnly,
    pricingConfig,
    latestSubmissionHash,
    submissionHash,
    submission,
  ]);

  const data = result?.data;

  useEffect(() => {
    if (data != null && !isUserReadOnly) {
      dispatch(analyticsStore.update({ [pricingMethod]: data }));
    }
  }, [data, isUserReadOnly, pricingMethod, dispatch]);

  return {
    ...result,
    refetch: () =>
      fetchPricing(
        triggerAnalytics,
        isUserReadOnly,
        pricingConfig?.engine,
        pricingConfig?.metric,
        submission
      ),
  };
};
